import { partition as _partition } from "lodash-es";
import { createSelector } from "reselect";

const getHumansFilterQuery = (state) => state.humansFilterQuery;
export const getHumans = (state) => state.humans;
const getHumanComments = (state) => state.humanComments;
export const getMatchHumanId = (state, props) => props.match.params.humanId;
const getHumanEvents = (state) => state.events;
export const getCircles = (state) => state.circles;

export const getHumansCount = createSelector(
  [getHumans],
  (humans) => !!humans && humans.length
);

const addMatchingHumanIdsToArray = (
  positiveItems,
  negativeItems,
  items,
  key,
  outKey,
  positiveQuery,
  negativeQuery
) => {
  for (let item of items) {
    if (!item[key]) continue;

    const text = item[key].toLowerCase().replace(/\s+/g, " ");

    if (negativeQuery.some((q) => text.includes(q))) {
      negativeItems.push(item[outKey]);
      continue;
    }

    if (
      positiveQuery.length === 0 ||
      positiveQuery.every((q) => text.includes(q))
    ) {
      positiveItems.push(item[outKey]);
      continue;
    }
  }

  return [positiveItems, negativeItems];
};

export const extractPositiveNegativeQuery = (queryStr) => {
  // "HELLO -World a -b -" -> [["hello", "a"], ["world", "b"]]
  const [positiveQuery, negativeQueryDirty] = _partition(
    queryStr
      .toLowerCase()
      .split(" ")
      .filter((item) => !!item),
    (s) => !s.startsWith("-")
  ); // split into positive and neg

  // remove starting "-" from negative
  const negativeQuery = negativeQueryDirty
    .map((s) => s.slice(1))
    .filter((item) => !!item);

  return [positiveQuery, negativeQuery];
};

const getFilteredHumansFn = (query, humans, humanComments, humanEvents) => {
  if (!query || !query.searchTerm) {
    return humans;
  }

  let queryStr = query.searchTerm;
  const [positiveQuery, negativeQuery] = extractPositiveNegativeQuery(queryStr);

  const positiveIds = [];
  const negativeIds = [];

  addMatchingHumanIdsToArray(
    positiveIds,
    negativeIds,
    humans,
    "name",
    "id",
    positiveQuery,
    negativeQuery
  );

  // humans with matching name should be sorted on top
  const namePositiveIds = [...positiveIds];

  addMatchingHumanIdsToArray(
    positiveIds,
    negativeIds,
    humanComments,
    "text",
    "humanId",
    positiveQuery,
    negativeQuery
  );

  addMatchingHumanIdsToArray(
    positiveIds,
    negativeIds,
    humanEvents,
    "label",
    "humanId",
    positiveQuery,
    negativeQuery
  );

  let filteredHumans = humans.filter(
    (h) => positiveIds.includes(h.id) && !negativeIds.includes(h.id)
  );
  // assuming stable sort, so previous sorting order is not destoryed
  filteredHumans.sort(
    (a, b) =>
      (namePositiveIds.includes(b.id) ? 1 : 0) -
      (namePositiveIds.includes(a.id) ? 1 : 0)
  );

  return filteredHumans;
};

// updated date = human or comment or event
export const getHumansLastUpdatedDate = createSelector(
  [getHumans, getHumanComments, getHumanEvents],
  (humans, comments, events) => {
    const out = {}; // human id -> number

    for (let h of humans) {
      out[h.id] = new Date(h.modifiedAt).getTime();
    }

    for (let c of comments) {
      out[c.humanId] = Math.max(out[c.humanId], new Date(c.modifiedAt));
    }

    for (let e of events) {
      out[e.humanId] = Math.max(out[e.humanId], new Date(e.modifiedAt));
    }

    return out;
  }
);

export const getSortedHumans = createSelector(
  [getHumans, getHumansLastUpdatedDate],
  (humans, humanLastUpdated) => {
    const humansList = humans.map((h) => ({
      ...h,
      computedUpdatedDate: humanLastUpdated[h.id],
    }));

    return humansList.sort(
      (a, b) => b.computedUpdatedDate - a.computedUpdatedDate
    );
  }
);

export const getFilteredHumans = createSelector(
  [getHumansFilterQuery, getSortedHumans, getHumanComments, getHumanEvents],
  getFilteredHumansFn
);

export const getSelectedHuman = createSelector(
  [getHumans, getMatchHumanId],
  (humans, humanId) => humans.find((item) => item.id === humanId)
);
